import { useEffect, useRef, useState } from 'react';
import { resolveDripTemplateSession } from '../api/dripTemplateSession';
export const identityKey = (target) => `${target.drip_id}:${target.version_number ?? 'latest'}:${target.step_id}:${target.channel}`;
/**
 * Exported and tested on its own, separate from React's effect timing:
 * React renders once with a NEW target but the OLD stored state before
 * the identity-change effect gets a chance to run. This is what that
 * render sees instead of the mismatched stored state — a pure lookup,
 * so proving it right doesn't depend on when useEffect happens to fire.
 */
export const selectSessionState = (stored, target) => stored.key === identityKey(target) ? stored.state : { status: 'loading' };
/**
 * Owns exactly one authoritative resolution per (drip_id, version_number, step_id, channel)
 * identity. The effect below is the ONLY thing that starts a new
 * resolution — it depends solely on that identity, so a background
 * refetch, an invalidation from a save elsewhere, or a window-focus
 * refresh cannot re-trigger it. Once `state.status === 'ready'`, nothing
 * in the app can cause this hook to reconsider that result except the
 * identity itself changing (a real target switch) or an explicit `retry`.
 *
 * Stored state is tagged with the identity it was resolved for. On a
 * target switch, React renders once with the NEW target but the OLD
 * state before this effect gets a chance to run — the tag comparison
 * below is what stops that render from handing the old target's data to
 * the new one; it doesn't wait for the effect to catch up.
 *
 * `deps` is read from a ref so a caller passing a freshly-constructed
 * object every render doesn't spuriously restart the resolution — only
 * the target identity does that.
 */
const useDripTemplateSession = (target, deps) => {
    const [stored, setStored] = useState(() => ({
        key: identityKey(target),
        state: { status: 'loading' }
    }));
    const generationRef = useRef(0);
    const targetRef = useRef(target);
    const depsRef = useRef(deps);
    targetRef.current = target;
    depsRef.current = deps;
    const run = () => {
        const generation = ++generationRef.current;
        const key = identityKey(targetRef.current);
        void resolveDripTemplateSession(depsRef.current, targetRef.current, () => generationRef.current === generation, (state) => setStored({ key, state }));
    };
    // Identity only — see the module comment above. The cleanup bumps the
    // generation too, so a resolution still in flight when this effect
    // tears down (target change OR full unmount) can never commit its
    // result, not just the ones started by a later run().
    useEffect(() => {
        run();
        return () => {
            generationRef.current += 1;
        };
    }, [target.drip_id, target.version_number, target.step_id, target.channel]);
    return { state: selectSessionState(stored, target), retry: run };
};
export default useDripTemplateSession;
